<script setup>
/**
 * MerakezSelector Component
 * Displays a searchable list of Assortments (merakez) for selection
 */
import { ref, computed } from 'vue'

const props = defineProps({
  assortments: {
    type: Array,
    default: () => [],
  },
  selectedMerakez: {
    type: String,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['select'])

// Local search state
const searchQuery = ref('')

// Filtered assortments based on search
const filteredAssortments = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) {
    return props.assortments
  }

  return props.assortments.filter((item) => {
    const idMatch = item.id.toLowerCase().includes(query)
    const nameMatch = item.name?.toLowerCase().includes(query)
    return idMatch || nameMatch
  })
})

// Get display name for an assortment
function getDisplayName(item) {
  return item.name || `מרכז ${item.id}`
}

// Handle selection
function handleSelect(merakez) {
  emit('select', merakez)
}

// Clear search
function clearSearch() {
  searchQuery.value = ''
}
</script>

<template>
  <div class="merakez-selector">
    <!-- Header -->
    <div class="selector-header">
      <h2 class="selector-title">לקוחות מרכז (מגוונים)</h2>
      <span v-if="!loading && !error" class="selector-count">{{ assortments.length }}</span>
    </div>

    <!-- Search Input -->
    <div class="selector-search">
      <div class="search-input-wrapper">
        <svg
          class="search-icon"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="חיפוש לפי מספר או שם..."
        />
        <button v-if="searchQuery" class="search-clear-btn" @click="clearSearch">
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="selector-loading">
      <div class="loading-spinner"></div>
      <span>טוען מגוונים...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="selector-error">
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4M12 16h.01" />
      </svg>
      <span>{{ error }}</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredAssortments.length === 0" class="selector-empty">
      <span v-if="searchQuery">לא נמצאו תוצאות עבור "{{ searchQuery }}"</span>
      <span v-else>אין מגוונים להצגה</span>
    </div>

    <!-- Assortments List -->
    <div v-else class="selector-list">
      <button
        v-for="item in filteredAssortments"
        :key="item.id"
        class="selector-item"
        :class="{ selected: selectedMerakez === item.id }"
        @click="handleSelect(item.id)"
      >
        <span class="item-id">{{ item.id }}</span>
        <span class="item-name">{{ getDisplayName(item) }}</span>
        <svg
          v-if="selectedMerakez === item.id"
          class="item-check"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
.merakez-selector {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
}

/* Header */
.selector-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.selector-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
}

.selector-count {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  background: var(--bg-tertiary);
  padding: 2px 8px;
  border-radius: var(--border-radius-full);
}

/* Search */
.selector-search {
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  height: 36px;
  padding: 0 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.search-input-wrapper:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.search-icon {
  flex-shrink: 0;
  color: var(--text-muted);
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  outline: none;
  text-align: right;
}

.search-input::placeholder {
  color: var(--text-muted);
}

.search-clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.search-clear-btn:hover {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

/* Loading */
.selector-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xl);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error */
.selector-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-lg);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  text-align: center;
}

/* Empty */
.selector-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xl);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  text-align: center;
}

/* List */
.selector-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--spacing-xs);
}

.selector-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--border-radius);
  font-family: var(--font-family);
  text-align: right;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.selector-item:hover {
  background: var(--bg-secondary);
}

.selector-item.selected {
  background: rgba(99, 102, 241, 0.1);
  border-color: var(--color-primary);
}

.item-id {
  flex-shrink: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family-mono, monospace);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  padding: 2px 8px;
  border-radius: var(--border-radius-sm);
}

.item-name {
  flex: 1;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-check {
  flex-shrink: 0;
  color: var(--color-primary);
}
</style>
